import React, { useEffect, useRef, useState } from "react";
import { X, DoorOpen, Square, Sparkles, Combine } from "lucide-react";

/**
 * V2.0.7 — Wall-click add-opening menu.
 *
 * Two stages:
 *   1. Category picker (only shown for EXTERIOR walls): Window | Door
 *      Interior walls skip straight to the door-type picker.
 *   2. Type picker — 8 door types or 8 window types as in OpeningEditor.
 *
 * Calls `onPick({ kind: "window"|"door", type })` once the user has
 * selected. The caller is responsible for inserting the opening into
 * the floor's doors/windows array using the wall context it already has.
 *
 * Auto-flips ABOVE the click anchor when the menu would overflow the
 * viewport (front-wall openings have low screen-y).
 */
const DOOR_TYPES = [
  { id: "swing",         label: "Swing" },
  { id: "single",        label: "Single Entry" },
  { id: "french",        label: "French" },
  { id: "dutch",         label: "Dutch" },
  { id: "sliding",       label: "Sliding" },
  { id: "sliding_glass", label: "Glass Slider" },
  { id: "pocket",        label: "Pocket" },
  { id: "bifold",        label: "Bifold" },
];
const INTERIOR_DOOR_TYPES = [
  { id: "swing",   label: "Swing" },
  { id: "pocket",  label: "Pocket" },
  { id: "sliding", label: "Sliding" },
  { id: "bifold",  label: "Bifold" },
];
const WINDOW_TYPES = [
  { id: "fixed",       label: "Fixed" },
  { id: "single_hung", label: "Single-Hung" },
  { id: "double_hung", label: "Double-Hung" },
  { id: "sliding",     label: "Sliding" },
  { id: "casement",    label: "Casement" },
  { id: "awning",      label: "Awning" },
  { id: "hopper",      label: "Hopper" },
  { id: "bay",         label: "Bay" },
];

export default function WallAddMenu({
  wallKind, // "exterior" | "interior"
  anchorX,
  anchorY,
  recommendation,   // V2.0.8 — { category, type, label, why } from openingRecommendations
  mergeOption,      // V2.0.10 — { roomAName, roomBName } when interior wall is mergeable
  onPick,
  onMerge,
  onClose,
}) {
  const ref = useRef(null);
  // Interior walls go directly to the door type picker.
  // Exterior walls start at category and reach the type picker after a pick.
  const [stage, setStage] = useState(wallKind === "interior" ? "interior-type" : "category");

  // Dismiss on outside click / Esc.
  useEffect(() => {
    const onDocDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("pointerdown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const vw = typeof window !== "undefined" ? window.innerWidth : 1920;
  const vh = typeof window !== "undefined" ? window.innerHeight : 1080;
  // Estimated menu height varies by stage; we err on the tall side so the
  // flip-above logic never traps the buttons off-screen.
  const estHeight = stage === "category" ? 130 : 240;
  const left = Math.min(anchorX, vw - 250);
  const wouldOverflow = anchorY + estHeight + 8 > vh;
  const top = wouldOverflow
    ? Math.max(8, anchorY - estHeight - 8)
    : Math.max(8, anchorY + 8);

  const pickType = (kind, type) => {
    onPick?.({ kind, type });
  };

  return (
    <div
      ref={ref}
      data-testid={`wall-add-menu-${stage}`}
      style={{ position: "fixed", left, top, zIndex: 60 }}
      className="w-60 border border-brass-500 bg-zinc-950 shadow-[0_12px_36px_rgba(0,0,0,0.6)]"
    >
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-zinc-800">
        <div className="font-mono text-[9px] uppercase tracking-widest text-brass-400">
          // {wallKind} wall · add opening
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="wall-add-menu-close"
          className="text-zinc-400 hover:text-zinc-100"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      {/* V2.0.8 — Smart-insert recommendation chip. Renders at the top
          of every menu stage; tapping it commits the recommended type
          without making the user drill into the type grid. The chip
          shows the type label + a short rationale ("Saves bathroom
          floor space", "IRC R310 egress", etc.) so the user understands
          WHY this default was chosen. */}
      {recommendation && (
        <button
          type="button"
          data-testid="wall-add-recommendation"
          onClick={() => {
            const kind = recommendation.category || recommendation.kind;
            pickType(kind === "window" ? "window" : "door", recommendation.type);
          }}
          className="w-full px-3 py-2 flex items-start gap-2 border-b border-zinc-800 bg-brass-950/40 hover:bg-brass-900/60 text-left transition-colors"
        >
          <Sparkles className="w-3.5 h-3.5 text-brass-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-widest text-brass-300">
              // Architect suggests
            </div>
            <div className="text-zinc-100 text-[12px] font-medium">
              {recommendation.label}
            </div>
            {recommendation.why && (
              <div className="text-zinc-400 text-[10px] truncate">
                {recommendation.why}
              </div>
            )}
          </div>
        </button>
      )}

      {/* V2.0.10 — One-click "Merge into open plan" for interior walls.
          Removes the dividing partition between the two adjacent rooms
          via the `merged_with` schema field. Renders ABOVE the door-type
          grid so the action is the most prominent option for interior
          walls (most users want a door; the merge is a power move). */}
      {mergeOption && wallKind === "interior" && (
        <button
          type="button"
          data-testid="wall-add-merge"
          onClick={() => onMerge?.()}
          className="w-full px-3 py-2 flex items-start gap-2 border-b border-zinc-800 bg-violet-950/40 hover:bg-violet-900/60 text-left transition-colors"
        >
          <Combine className="w-3.5 h-3.5 text-violet-300 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-widest text-violet-300">
              // Open plan
            </div>
            <div className="text-zinc-100 text-[12px] font-medium truncate">
              Merge {mergeOption.roomAName} + {mergeOption.roomBName}
            </div>
            <div className="text-zinc-400 text-[10px] truncate">
              Removes this wall for an L-shaped great room
            </div>
          </div>
        </button>
      )}

      {stage === "category" && (
        <div className="p-1.5 grid grid-cols-2 gap-1">
          <button
            type="button"
            data-testid="wall-add-category-window"
            onClick={() => setStage("window-type")}
            className="px-2 py-3 border border-zinc-700 text-zinc-200 hover:border-brass-500 hover:bg-brass-950/30 font-mono text-[10px] uppercase tracking-widest inline-flex flex-col items-center gap-1.5"
          >
            <Square className="w-4 h-4 text-brass-400" />
            Window
          </button>
          <button
            type="button"
            data-testid="wall-add-category-door"
            onClick={() => setStage("exterior-type")}
            className="px-2 py-3 border border-zinc-700 text-zinc-200 hover:border-brass-500 hover:bg-brass-950/30 font-mono text-[10px] uppercase tracking-widest inline-flex flex-col items-center gap-1.5"
          >
            <DoorOpen className="w-4 h-4 text-brass-400" />
            Door
          </button>
        </div>
      )}

      {stage === "window-type" && (
        <TypeGrid
          testidPrefix="wall-add-window-type"
          options={WINDOW_TYPES}
          onPick={(id) => pickType("window", id)}
        />
      )}
      {stage === "exterior-type" && (
        <TypeGrid
          testidPrefix="wall-add-door-type"
          options={DOOR_TYPES}
          onPick={(id) => pickType("door", id)}
        />
      )}
      {stage === "interior-type" && (
        <TypeGrid
          testidPrefix="wall-add-door-type"
          options={INTERIOR_DOOR_TYPES}
          onPick={(id) => pickType("door", id)}
        />
      )}
    </div>
  );
}

function TypeGrid({ options, testidPrefix, onPick }) {
  return (
    <div className="p-1.5 grid grid-cols-2 gap-1">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          data-testid={`${testidPrefix}-${o.id}`}
          onClick={() => onPick(o.id)}
          className="px-2 py-1.5 border border-zinc-700 text-zinc-300 hover:border-brass-500 hover:bg-brass-950/30 font-mono text-[10px] uppercase tracking-widest text-left transition-colors"
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
